import {GetServerSideProps, InferGetServerSidePropsType} from "next";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import {ReactElement, useRef, useState} from "react";
import Layout from "../../components/common/Layout";
import VermietungDetailedTitle from "../../components/VermietungDetailedTitle/VermietungDetailedTitle";
import VermietungsCarousel from "../../components/VermietungsCarousel/VermietungsCarousel";
import VermietungsDetailedTabGroup from "../../components/VermietungsDetailedTabGroup/VermietungsDetailedTabGroup";
import BuchungAnfrageMobileHeader from "../../components/VermietungBuchungAnfragenForm/BuchungAnfrageMobileHeader";
import VermietungBuchungAnfrageForm from "../../components/VermietungBuchungAnfragenForm/VermietungBuchungAnfrageForm";
import {NoImageAvailablePlaceholder} from "../../data/StaticImages";
import {useRouter} from "next/router";
import {ImmoImageTypes, ServerSideImageFetch} from "../../helpers/ImageHelpers";
import {getBookings, getObjectInServerSideProps} from "../../data/ReactQueries";
import {calcPersonSleepingBathrooms, getBookedDates} from "../../components/CalculationHelper/CalcHelpers";
import {ObjectInterfaceWithCity} from "../../interfaces/ObjectInterface";
import {getSafeLocale} from "../../helpers/LocalizeObject";
import Head from "next/head";
import {EXTERNAL_DATA_URL} from "../sitemap.xml";
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from "next-i18next";
import HomeBanner from "../../components/common/HomeBanner"
import HomeMarinaAmMittelmeer from "../../components/common/HomeMarinaAmMittelmeer";
import HomeKaufenUndVerkaufen from "../../components/common/HomeKaufenUndVerkaufen";
import LastMinutes, {OfferInterface} from "../../components/common/LastMinutes";
import ImmobilieKaufenHome, {ImmobilieKaufenHomePropsInterface} from "../../components/common/ImmobilieKaufenHome";
import {Filter} from "../../data/Filter/Filter.config";
import {fetchItem} from "../../data/ReactQueries";

export default function Page<NextPageWithLayout>({serverSideImages, objectData, incomingStartDate, incomingEndDate, incomingNumberOfPeople, locale}: InferGetServerSidePropsType<typeof getServerSideProps>) {
	const fetchedObject: ObjectInterfaceWithCity = JSON.parse(objectData);
	const router = useRouter();
	const images = serverSideImages;

	const [startDate, setStartDate] = useState<Date | null>(
		incomingStartDate ? new Date(parseInt(incomingStartDate) * 1000) : null
	);
	const [endDate, setEndDate] = useState<Date | null>(
		incomingEndDate ? new Date(parseInt(incomingEndDate) * 1000) : null
	);
	const [adultCount, setAdultCount] = useState(incomingNumberOfPeople ?? 1);
	const [kidsCount, setKidsCount] = useState(0);
	const personSleepingBathrooms = calcPersonSleepingBathrooms(fetchedObject);

	const {data: filtedBookingsByObjectId} = useQuery(["bookings", fetchedObject?.id?.toString()], () => getBookings(fetchedObject?.id?.toString()), {enabled: Boolean(fetchedObject?.id)});
	const bookedDates: { start: Date; end: Date }[] | undefined = getBookedDates(filtedBookingsByObjectId);

	const initialStartDateRef = useRef(startDate);
	const initialEndDateRef = useRef(endDate);

	const {t} = useTranslation();
	const {
		data: objects,
		isLoading: objectsLoading,
		refetch
	} = useQuery(
		["lastMinutes", {[Filter.Rent.queryKey]: 1, locale: router.locale}],
		(): Promise<OfferInterface[]> => {
			return fetchItem(
				"last-minutes",
				{
					[Filter.Rent.queryKey]: 1
				},
				router.locale
			);
		}
	);

	const {
		data: SaleObjects,
	} = useQuery(
		[
			"SaleObjects",
			{[Filter.Sale.queryKey]: 1, locale: router.locale}
		],
		() => {
			return fetchItem(
				"objects",
				{
					[Filter.Sale.queryKey]: 1
				},
				router.locale
			);
		}
	);

	if (!fetchedObject) return <></>;

	const jsonLd = {
		"@context": "http://schema.org",
		"@type": "VacationRental",
		brand: {
			"@type": "Brand",
			"name": "LEON Inmobiliaria"
		},
		name: fetchedObject.headline,
		description: fetchedObject.description,
		url: `${EXTERNAL_DATA_URL}/${router.asPath}`,
		image: images[0]
	};
	const addressParts = [
		fetchedObject?.street,
		fetchedObject?.place,
		fetchedObject?.cityOfObject?.name,
		fetchedObject?.cityOfObject?.province?.name,
	];

	const address = addressParts.filter(Boolean).join(', ');

	const LatLng = fetchedObject?.gmaps || address;

	const mapUrl = LatLng ? `https://www.google.com/maps?q=${encodeURIComponent(LatLng)}&makers=${encodeURIComponent(LatLng)}&hl=${locale}&region=${locale}&output=embed` : null;
	const mapLink = LatLng ? `https://www.google.com/maps?q=${encodeURIComponent(LatLng)}&makers=${encodeURIComponent(LatLng)}&hl=${locale}&region=${locale}` : null;

	return (
		<>
			<div className="product-page-vermietung">
				<Head>
					<title>{fetchedObject.description}</title>
					<script
						type="application/ld+json"
						dangerouslySetInnerHTML={{__html: JSON.stringify(jsonLd, null, 2)}}
					/>
				</Head>
				<VermietungsCarousel images={images} object={fetchedObject}/>
				<BuchungAnfrageMobileHeader
					endDate={endDate}
					objectId={router.query.id as string}
					startDate={startDate}
				/>
				<VermietungBuchungAnfrageForm
					objectId={fetchedObject.id}
					adultCount={adultCount}
					kidsCount={kidsCount}
					onAdultCountChange={setAdultCount}
					onKidsCountChange={setKidsCount}
					startDate={startDate}
					endDate={endDate}
					onStartDateChange={setStartDate}
					onEndDateChange={setEndDate}
				/>

				{/* <div className="lg:pt-[650px]"/> */}

				<div className="w-full max-w-[90%] mx-auto">
					<div className="">
						<div className="col-span-2">
							<VermietungDetailedTitle
								furnishing={personSleepingBathrooms}
								objectId={fetchedObject.object_id}
								tln={(fetchedObject.tln && fetchedObject.tln + " / ")}
								title={fetchedObject.headline}
								object={fetchedObject}
							/>
							<VermietungsDetailedTabGroup object={fetchedObject} bookedDates={bookedDates} locale={locale} startDate={initialStartDateRef.current} endDate={initialEndDateRef.current}/>
						</div>
					</div>
					<div className="pb-1 mt-10">
						<div className="flex justify-center md:justify-between items-end mb-8">
							{mapLink && (
								<a href={mapLink} className="hidden md:flex">
									<img 
										src="/images/map-icon.svg"
										alt="Map Icon"
										title="Map Icon"
										className="w-8 h-8"
									/>
									<span className="text-[16px] underline text-black">{t("common.empuriabrava")}</span>
								</a>
							) }
							{mapLink && (
								<h2 className="font-light text-[40px] md:text-[48px]">
									{t("common.sitePlan")}
								</h2>
							)}
							<span className="w-[138px] hidden md:block"></span>
						</div>
						{mapUrl &&
							<div className="w-full h-96 rounded-[20px] shadow-[0px_1px_6px_rgba(0,0,0,0.1607843137)]">
								<iframe
									src={mapUrl}
									width="100%"
									height="100%"
									allowFullScreen
									loading="lazy"
									referrerPolicy="no-referrer-when-downgrade"
									className="rounded-[20px]"
								></iframe>
							</div>
						}
						<div className="mt-10" />
						<HomeBanner />
						<div className="mt-10" />
					</div>
				</div>
				
				<div className="w-full md:max-w-[90%] pl-4 md:pl-0 mx-auto">
					<LastMinutes objects={objects ?? []}/>
					<div className="mt-12" />
					<ImmobilieKaufenHome objects={SaleObjects?.items ?? []}/>
					<div className="mt-10" />
				</div>

				<HomeMarinaAmMittelmeer />
				
				<HomeKaufenUndVerkaufen />
			</div>
		</>
	);
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	const {query} = context;
	const {id: objectId, from_date, to_date, min_persons_total} = query;

	const safeLocale = getSafeLocale(context.locale)
	const object = await getObjectInServerSideProps(
		parseInt(objectId as string),
		safeLocale
	);

	const resp = await ServerSideImageFetch(
		parseInt(objectId as string),
		ImmoImageTypes.IMAGE
	);
	let tmpImages = {
		main: [NoImageAvailablePlaceholder],
		others: []
	};
	if (resp.ok) {
		tmpImages = await resp.json();
	}

	const images = [...tmpImages.main, ...tmpImages.others].map(
		(filename) => filename
	)

	return {
		props: {
			locale: safeLocale,
			serverSideImages: images,
			objectData: object ?? null,
			incomingStartDate: from_date ?? null,
			incomingEndDate: to_date ?? null,
			incomingNumberOfPeople: min_persons_total ?? null,
			...(await serverSideTranslations(
				getSafeLocale(context.locale) as string,
				["common"]
			))
			// object,
		}
	};
};

// export const getStaticProps: GetStaticProps = async (context) => {
//   return {
//     props: {
//       ...(await serverSideTranslations(getSafeLocale(context.locale) as string, ["common"])),
//       // object,
//     },
//   };
// };

// export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
//   return {
//     paths: [], //indicates that no page needs be created at build time
//     fallback: "blocking", //indicates the type of fallback
//   };
// };

Page.getLayout = function getLayout(page: ReactElement) {
	return (
		<Layout 
			headerNotTransparent={false}
			hasShadow={true}
		>
			{page}
		</Layout>
	);
};
